import { Button, Spinner } from "@heroui/react";
import { GENRE_LABELS } from "@storyline/shared";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import PageHeader from "@/src/common/components/page-header";
import Pill from "@/src/common/components/pill";
import { STORY_STATUS_LABELS, STORY_STATUS_TONES } from "@/src/common/constants/studio-labels";
import CreateSeriesPanel from "@/src/series/components/create-series-panel";
import { useSeriesList } from "@/src/series/hooks/use-series-list";

const formatDate = (value: string): string =>
	new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });

const SeriesListPage: React.FC = () => {
	const navigate = useNavigate();
	const { series, isLoading, hasError, reload } = useSeriesList();
	const [isCreating, setIsCreating] = useState(false);

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Séries"
				description="Chaque série porte sa bible, ses golden assets et ses épisodes."
				actions={<Button onPress={() => setIsCreating((previous) => !previous)}>{isCreating ? "Fermer" : "Nouvelle série"}</Button>}
			/>

			{isCreating ? (
				<CreateSeriesPanel
					onCreated={(seriesId) => {
						setIsCreating(false);
						navigate(`/series/${seriesId}`);
					}}
					onCancel={() => setIsCreating(false)}
				/>
			) : null}

			{isLoading ? (
				<div className="flex justify-center py-16">
					<Spinner size="lg" />
				</div>
			) : hasError ? (
				<div className="flex flex-col items-center gap-3 rounded-xl border border-border-soft bg-surface p-10 text-center">
					<p className="text-danger text-sm">Impossible de charger les séries.</p>
					<Button variant="secondary" onPress={() => void reload()}>
						Réessayer
					</Button>
				</div>
			) : series.length === 0 ? (
				<p className="rounded-xl border border-border-soft bg-surface p-10 text-center text-muted text-sm">Aucune série pour l'instant.</p>
			) : (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{series.map((item) => (
						<Link
							key={item.id}
							to={`/series/${item.id}`}
							className="flex flex-col gap-3 rounded-xl border border-border-soft bg-surface p-4 transition hover:border-accent-lav"
						>
							<div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-glass">
								{item.coverUrl ? (
									<img src={item.coverUrl} alt="" className="size-full object-cover" />
								) : (
									<span className="text-muted text-xs">Sans couverture</span>
								)}
							</div>
							<div className="flex items-start justify-between gap-2">
								<span className="font-semibold text-foreground">{item.title}</span>
								<Pill tone={STORY_STATUS_TONES[item.status]}>{STORY_STATUS_LABELS[item.status]}</Pill>
							</div>
							<div className="flex items-center justify-between text-muted text-xs">
								<span>{GENRE_LABELS[item.genre]}</span>
								<span>
									{item.episodesCount} épisode(s) · {formatDate(item.updatedAt)}
								</span>
							</div>
						</Link>
					))}
				</div>
			)}
		</div>
	);
};

export default SeriesListPage;
